// qs-LayerItem.vue

<template>
  <div
    class="layer-item"
    :class="getLayerItemClasses()"
    @click="handleLayerClick"
    @mouseleave="handleMouseLeave"
  >
    
    <div class="layer-indicator-bar"></div>
    
    
    <div class="content-indicator">
      <FileBox
        v-if="hasContent"
        class="content-icon"
        title="Layer has content"
      />
      <File
        v-else
        class="empty-icon"
        title="Empty layer"
      />
    </div>

    
    <div class="arrow-controls">
      
      <div class="arrow-row">
        
        <button
          class="arrow-btn double-up"
          :disabled="!canMoveUp || isProcessing"
          title="Move to top"
          @click.stop="handleMoveToTop"
        >
          <ChevronsUp />
        </button>

        
        <button
          class="arrow-btn single-up"
          :disabled="!canMoveUp || isProcessing"
          title="Move up one position"
          @click.stop="handleMoveUp"
        >
          <ChevronUp />
        </button>
      </div>

      
      <div class="arrow-row">
        
        <button
          class="arrow-btn double-down"
          :disabled="!canMoveDown || isProcessing"
          title="Move to bottom"
          @click.stop="handleMoveToBottom"
        >
          <ChevronsDown />
        </button>

        
        <button
          class="arrow-btn single-down"
          :disabled="!canMoveDown || isProcessing"
          title="Move down one position"
          @click.stop="handleMoveDown"
        >
          <ChevronDown />
        </button>
      </div>
    </div>

    
    <div
      class="layer-name"
      :title="isEditing ? 'Press Enter to save, Escape to cancel' : 'Double-click or long-press to rename'"
      @dblclick.stop="handleStartRename"
      @mousedown="startRenameLongPress"
      @mouseup="endRenameLongPress"
      @mouseleave="endRenameLongPress"
      @touchstart.passive="startRenameLongPress"
      @touchend="endRenameLongPress"
      @touchcancel="endRenameLongPress"
    >
      <span v-if="!isEditing">{{ layer.name }}</span>
      <input
        v-else
        ref="nameInputRef"
        v-model="editingName"
        class="layer-name-input"
        @blur="handleFinishRename"
        @keyup.enter="handleFinishRename"
        @keyup.escape="handleCancelRename"
      >
    </div>

    
    <div class="action-controls">
      
      <button
        class="action-btn visibility-btn"
        :class="{ 'active': !layer.visible }"
        :title="layer.visible ? 'Hide layer' : 'Show layer'"
        :disabled="isProcessing"
        @click.stop="handleToggleVisibility"
      >
        <Eye
          v-if="layer.visible"
        />
        <EyeOff
          v-else
        />
      </button>

      
      <button
        class="action-btn lock-btn"
        :class="{ 'active': layer.locked }"
        :title="layer.locked ? 'Unlock layer' : 'Lock layer'"
        :disabled="isProcessing"
        @click.stop="handleToggleLock"
      >
        <Lock
          v-if="layer.locked"
        />
        <Unlock
          v-else
        />
      </button>

      
      <button
        class="action-btn duplicate-btn"
        :disabled="isProcessing"
        title="Duplicate layer"
        @click.stop="handleDuplicate"
      >
        <Copy />
      </button>

      
      <button
        class="action-btn delete-btn"
        :disabled="!canDelete || layer.locked"
        :title="getDeleteButtonTitle()"
        @click.stop="handleDelete"
      >
        <Trash2 />
      </button>

      
      <button
        class="action-btn move-btn"
        :class="{ 'active': isPositionModeActive }"
        :disabled="isProcessing || layer.locked"
        :title="getMoveButtonTitle()"
        @pointerup.stop="handleLayerPosition"
      >
        <Move />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Layer } from '@/types/qs-types-index'
import { ChevronDown, ChevronsDown, ChevronsUp, ChevronUp, Copy, Eye, EyeOff, File, FileBox, Lock, Move, Trash2, Unlock } from 'lucide-vue-next'
import { computed, nextTick, onUnmounted, ref } from 'vue'
import { useModeManager } from '@/composables/qs-useModeManager'
import { useLayerStore } from '@/stores/qs-store-layers'

interface Props {
  layer: Layer
  index: number
  isSelected: boolean
  isProcessing: boolean
  canDelete: boolean
  totalLayers: number
}

const props = defineProps<Props>()

const emit = defineEmits<{
  'layer-click': [layerId: string]
  'toggle-visibility': [layerId: string]
  'toggle-lock': [layerId: string]
  'duplicate': [layerId: string]
  'delete': [layerId: string]
  'move': [layerId: string]
  'start-rename': [layerId: string]
  'finish-rename': [layerId: string, newName: string]
  'cancel-rename': []
  'move-up': [layerId: string, index: number]
  'move-down': [layerId: string, index: number]
  'move-to-top': [layerId: string, index: number]
  'move-to-bottom': [layerId: string, index: number]
}>()

const isEditing = ref(false)
const editingName = ref('')
const nameInputRef = ref<HTMLInputElement | null>(null)
const forceUpdate = ref(0)

let renameLongPressTimer: ReturnType<typeof setTimeout> | null = null
const renameLongPressDelay = 500

const modeManager = useModeManager()
const layerStore = useLayerStore()

const hasContent = computed(() => {
  const paths = props.layer.paths
  return Array.isArray(paths) && paths.length > 0
})

const isPositionModeActive = computed(() => {
  return modeManager.activeMode.value === 'position' && modeManager.globalModeState.value.activeLayerId === props.layer.id
})

const canMoveUp = computed(() => props.index > 0)

const canMoveDown = computed(() => props.index < props.totalLayers - 1)

const getLayerItemClasses = () => ({
  active: props.isSelected,
  locked: props.layer.locked,
  editing: isEditing.value,
  'selection-disabled': props.isProcessing || props.layer.locked
})

const getDeleteButtonTitle = () => {
  if (props.layer.locked) {
    return 'Cannot delete locked layer - unlock first'
  }
  if (!props.canDelete) {
    return 'Cannot delete - only one layer remaining'
  }
  return 'Delete layer'
}

const getMoveButtonTitle = () => {
  if (props.layer.locked) {
    return 'Cannot move locked layer - unlock first'
  }
  if (isPositionModeActive.value) {
    return 'Position mode ON - Click to exit'
  }
  return 'Move layer'
}

const handleLayerClick = () => {
  if (isEditing.value) return
  if (props.layer.locked) {
    console.warn('Cannot select locked layer:', props.layer.name)
    return
  }
  emit('layer-click', props.layer.id)
}

const handleToggleVisibility = () => {
  emit('toggle-visibility', props.layer.id)
}

const handleToggleLock = () => {
  emit('toggle-lock', props.layer.id)
}

const handleDuplicate = () => {
  emit('duplicate', props.layer.id)
}

const handleDelete = () => {
  emit('delete', props.layer.id)
}

const handleLayerPosition = async () => {
  if (props.layer.locked) {
    return
  }

  const wasThisLayerActive = isPositionModeActive.value
  
  if (wasThisLayerActive) {
    console.log('Position mode OFF:', props.layer.name)
    await modeManager.switchToDrawingMode()
  } else {
    if (modeManager.activeMode.value === 'position') {
      const currentActiveLayer = modeManager.globalModeState.value.activeLayerId
      if (currentActiveLayer) {
        const layerData = layerStore.layers.find(l => l.id === currentActiveLayer)
        console.log('Position mode OFF:', layerData?.name || currentActiveLayer)
      }
      await modeManager.switchToDrawingMode()
    }

    console.log('Position mode ON:', props.layer.name)
    await modeManager.activatePositionMode(props.layer.id)
  }

  forceUpdate.value++
}

const startRenameLongPress = (event: MouseEvent | TouchEvent) => {
  if (isEditing.value || props.layer.locked) return
  if (event instanceof MouseEvent) {
    event.preventDefault()
    event.stopPropagation()
  }
  if (renameLongPressTimer) {
    clearTimeout(renameLongPressTimer)
  }
  renameLongPressTimer = setTimeout(() => {
    renameLongPressTimer = null
    handleStartRename()
  }, renameLongPressDelay)
}

const endRenameLongPress = () => {
  if (renameLongPressTimer) {
    clearTimeout(renameLongPressTimer)
    renameLongPressTimer = null
  }
}

const handleStartRename = () => {
  if (props.layer.locked) return
  isEditing.value = true
  editingName.value = props.layer.name
  emit('start-rename', props.layer.id)

  nextTick(() => {
    nameInputRef.value?.focus()
    nameInputRef.value?.select()
  })
}

const handleFinishRename = () => {
  if (!editingName.value.trim()) {
    handleCancelRename()
    return
  }

  emit('finish-rename', props.layer.id, editingName.value.trim())
  isEditing.value = false
  editingName.value = ''
}

const handleCancelRename = () => {
  isEditing.value = false
  editingName.value = ''
  emit('cancel-rename')
}

const handleMoveUp = () => {
  emit('move-up', props.layer.id, props.index)
}

const handleMoveDown = () => {
  emit('move-down', props.layer.id, props.index)
}

const handleMoveToTop = () => {
  emit('move-to-top', props.layer.id, props.index)
}

const handleMoveToBottom = () => {
  emit('move-to-bottom', props.layer.id, props.index)
}

const handleMouseLeave = (event: MouseEvent) => {
  const target = event.target as HTMLElement
  if (target && (target.classList.contains('layer-item') ||
                 target.classList.contains('layer-name') ||
                 target.classList.contains('action-btn') ||
                 target.classList.contains('arrow-btn'))) {
    target.blur()
  }
}

const handleLayerUpdate = () => {
  forceUpdate.value++
}

nextTick(() => {
  window.addEventListener('layer-updated', handleLayerUpdate)
  window.addEventListener('layers-changed', handleLayerUpdate)
})

onUnmounted(() => {
  window.removeEventListener('layer-updated', handleLayerUpdate)
  window.removeEventListener('layers-changed', handleLayerUpdate)
})
</script>

<style scoped>
* {
  outline: none !important;
}


.layer-item {
  width: auto;
  min-width: theme('spacing.44');
  height: theme('spacing.11');
  display: flex;
  align-items: center;
  background-color: theme('colors.creme.lighter');
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 0;
  position: relative;
  gap: 0;
  padding: 6px;
}

.layer-indicator-bar {
  width: 4px;
  height: 100%;
  background-color: theme('colors.creme.hoverpanel');
  transition: background-color 0.2s ease;
  flex-shrink: 0;
}

.layer-item.active .layer-indicator-bar {
  background-color: theme('colors.success.DEFAULT') !important;
}

.layer-item:hover:not(:focus) {
  background-color: theme('colors.creme.DEFAULT');
}

.layer-item:focus {
  background-color: theme('colors.creme.dark');
  outline: none;
}

.layer-item.active {
  background-color: theme('colors.creme.lighter');
}

.layer-item.locked {
  opacity: 0.7;
  background-color: theme('colors.creme.DEFAULT');
}

.layer-item.editing {
  background-color: theme('colors.creme.medium');
}

.layer-item.selection-disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.content-indicator {
  width: theme('spacing.8');
  height: theme('spacing.8');
  margin: theme('spacing.1.5') theme('spacing.1') theme('spacing.1.5') theme('spacing.1');
  border-radius: 0;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
}

.content-icon {
  color: theme('colors.text.light');
  opacity: 1;
  transition: all 0.2s ease;
  stroke-width: 1.5;
  stroke: currentColor;
  fill: none;
  width: 14px !important;
  height: 14px !important;
}

.content-icon:hover {
  color: theme('colors.text.DEFAULT');
}

.empty-icon {
  color: theme('colors.text.light');
  opacity: 0.8;
  transition: all 0.2s ease;
  stroke-width: 1.5;
  stroke: currentColor;
  fill: none;
  width: 14px !important;
  height: 14px !important;
}

.empty-icon:hover {
  color: theme('colors.text.DEFAULT');
  opacity: 1;
}

.arrow-controls {
  width: theme('spacing.5.5');
  height: theme('spacing.11');
  display: flex;
  flex-direction: column;
  margin-right: theme('spacing.1');
  flex-shrink: 0;
}

.arrow-row {
  display: flex;
  height: 50%;
}

.arrow-btn {
  width: 50%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  border-radius: theme('borderRadius.sm');
  transition: all 0.2s ease;
  opacity: 0.7;
  color: theme('colors.text.lighter');
  padding: 0;
  margin: 0;
  outline: none;
}

.arrow-btn svg {
  flex-shrink: 0;
  width: 14px !important;
  height: 14px !important;
  stroke-width: 1.5;
  stroke: currentColor;
  fill: none;
}

.arrow-btn:hover:not(:disabled):not(:focus) {
  background-color: theme('colors.creme.hoverbutton');
  opacity: 1;
  color: theme('colors.text.DEFAULT');
}

.arrow-btn:focus:not(:disabled) {
  background-color: theme('colors.creme.hoverbutton');
  opacity: 1;
  outline: none;
  color: theme('colors.text.DEFAULT');
}

.arrow-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
  color: theme('colors.text.lighter');
}

.arrow-btn:disabled:hover {
  background-color: transparent;
  color: theme('colors.text.lighter');
}

.layer-name {
  flex: 1;
  font-size: theme('fontSize.sm');
  color: theme('colors.text.DEFAULT');
  font-weight: theme('fontWeight.medium');
  padding: 0 theme('spacing.2') 0 theme('spacing.1');
  white-space: nowrap;
  display: flex;
  align-items: center;
  cursor: text;
  min-width: 0;
  margin-left: theme('spacing.1');
}

.layer-name:hover:not(:focus) {
  background-color: transparent;
}

.layer-name:focus {
  background-color: transparent;
  outline: none;
}

.layer-name-input {
  width: 100%;
  height: 100%;
  border: none;
  background: transparent;
  font-size: theme('fontSize.sm');
  font-weight: theme('fontWeight.medium');
  color: theme('colors.text.DEFAULT');
  outline: none;
  padding: 0;
  margin: 0;
}

.action-controls {
  display: flex;
  gap: 1px;
  flex-shrink: 0;
  margin-right: 0;
}

.action-btn {
  width: 24px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  border-radius: theme('borderRadius.DEFAULT');
  transition: all 0.2s ease;
  opacity: 1;
  color: theme('colors.text.lighter');
  padding: 0;
  margin: 0;
  outline: none;
}

.action-btn svg {
  flex-shrink: 0;
  width: 14px !important;
  height: 14px !important;
  stroke-width: 1.5;
  stroke: currentColor;
  fill: none;
}

.action-btn:hover:not(:disabled):not(:focus) {
  background-color: theme('colors.creme.hoverbutton');
  opacity: 1;
  color: theme('colors.text.DEFAULT');
}

.action-btn:focus:not(:disabled) {
  background-color: theme('colors.creme.hoverbutton');
  opacity: 1;
  outline: none;
  color: theme('colors.text.DEFAULT');
}

.action-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  color: theme('colors.text.lighter');
}

.action-btn:disabled:hover {
  background-color: transparent;
  color: theme('colors.text.lighter');
}

.visibility-btn.active {
  background-color: transparent;
  color: theme('colors.text.lighter');
}

.visibility-btn:not(.active) {
  color: #dc2626;
}

.lock-btn.active {
  background-color: transparent;
  color: #2563eb;
}

.move-btn.active {
  background-color: theme('colors.primary.500') !important;
  color: theme('colors.creme.light') !important;
}

.move-btn.active:hover {
  background-color: theme('colors.primary.600') !important;
  color: theme('colors.creme.light') !important;
}

.move-btn.active:focus {
  background-color: theme('colors.primary.600') !important;
  color: theme('colors.creme.light') !important;
}
</style>